/**
 * Public site (P6, PRD §6): the challenge configurator (ADR-0033), the rules page of a version, FAQ.
 * The stand is a demo behind the allowlist — "get started" leads to sign-in; no payments here.
 * Editorial language (ADR-0029): wide titles, one large price, lime for the one action that buys.
 */
import { Button, cn } from "@prop/ui";
import { ArrowLeft, ArrowRight, Plus } from "lucide-react";
import { Link, useRoute } from "wouter";
import { useLandingT, useLegalT, useTradeT } from "../../i18n";
import { fmtUsd } from "../../lib/fmt";
import { Figure, LIME, PANEL, PageHead } from "../money/parts";
import { Configurator } from "./configurator/Configurator";
import { useCatalog } from "./configurator/useConfigurator";
import { ProgramFacts, Row, sizeUsd } from "./ProgramFacts";

export function ProgramsPage() {
  const t = useTradeT();
  return (
    <main className="mx-auto flex w-full max-w-7xl flex-col gap-8 px-4 py-8 sm:px-6">
      <PageHead title={t("site.programs")} lead={t("site.programsLead")} />
      <Configurator />
      <ReadOn links={["faq", "terms", "execution", "payouts", "risk"]} />
    </main>
  );
}

export function RulesPage() {
  const t = useTradeT();
  const [, params] = useRoute("/programs/:slug");
  const q = useCatalog();
  const p = q.data?.programs.find((x) => x.slug === params?.slug);
  if (!p) return <main className="p-6 text-sm text-muted-foreground">{t("common.loading")}</main>;
  const b = p.rules.dayBoundary;
  return (
    <main className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-4 py-8 sm:px-6">
      <Link href="/programs" className="inline-flex items-center gap-1.5 self-start text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="size-4" aria-hidden="true" /> {t("site.programs")}
      </Link>
      <PageHead title={p.name} lead={`v${p.version} · ${t("site.dayEnds", { at: "session" in b ? "17:00 America/Chicago (CME)" : `${b.time} ${b.timezone}` })}`}>
        <Button asChild size="lg" className={LIME}>
          <Link href={`/buy?program=${p.slug}`}>
            {t("site.getStarted")} <ArrowRight aria-hidden="true" />
          </Link>
        </Button>
      </PageHead>
      <div className="grid items-start gap-4 md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
        <section className={cn(PANEL, "flex flex-col gap-6")}>
          <Figure value={sizeUsd(p.rules)} label={t("site.size")} />
          <dl>
            <Row
              k={t("site.price")}
              v={`${p.priceUsd ? fmtUsd(p.priceUsd) : "—"}${p.billing === "monthly" ? ` ${t("site.perMonth")}` : ""}`}
              note={p.launchPricing && p.listPriceUsd ? `${t("site.launch")} · ${fmtUsd(p.listPriceUsd)}` : undefined}
            />
          </dl>
        </section>
        <section className={PANEL}>
          <ProgramFacts rules={p.rules} full />
        </section>
      </div>
      <details className={cn(PANEL, "group text-sm")}>
        <summary className="flex cursor-pointer list-none items-center justify-between font-medium [&::-webkit-details-marker]:hidden">
          JSON
          <Plus className="size-4 transition-transform duration-200 group-open:rotate-45" aria-hidden="true" />
        </summary>
        <pre className="mt-4 overflow-auto text-xs">{JSON.stringify(p.rules, null, 2)}</pre>
      </details>
      {/* taste-skill-ignore: the rules hash is a literal code value */}
      <p className="break-all font-mono text-xs text-muted-foreground">{t("site.rulesHash", { hash: p.rulesHash })}</p>
    </main>
  );
}

/** Where to read on: the next pages of the public site (programs → questions → the documents that govern them). */
function ReadOn({ links }: { links: Array<"programs" | "faq" | "execution" | "payouts" | "terms" | "risk"> }) {
  const t = useTradeT();
  const tl = useLegalT();
  const tp = useLandingT();
  const label = (k: (typeof links)[number]) => (k === "programs" || k === "faq" ? t(`nav.${k}`) : tl(`nav.${k}`));
  return (
    <nav aria-labelledby="read-on" className="flex flex-col gap-3 border-t border-border pt-6">
      <h2 id="read-on" className="text-base font-semibold">
        {tp("docs.title")}
      </h2>
      <ul className="flex flex-wrap gap-2 text-sm">
        {links.map((k) => (
          <li key={k}>
            <Link href={k === "programs" || k === "faq" ? `/${k}` : `/legal/${k}`} className="inline-flex items-center gap-1.5 rounded-full border border-border px-3.5 py-1.5 hover:bg-muted">
              {label(k)} <ArrowRight className="size-3.5" aria-hidden="true" />
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/** The questions of the landing (the full answers), each with its own anchor; the FAQPage markup is built from them. */
export const FAQ_KEYS = ["real", "fills", "day", "rules", "feed", "payouts"] as const;

export function FaqPage() {
  const t = useTradeT();
  const tp = useLandingT();
  return (
    <main className="mx-auto flex w-full max-w-3xl flex-col gap-8 px-4 py-8 sm:px-6">
      <PageHead title={t("site.faq")} lead={t("site.faqLead")} />
      <div className={cn(PANEL, "py-1 sm:py-2")}>
        {FAQ_KEYS.map((k) => (
          <details key={k} id={k} className="group border-b border-border last:border-0">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-4 py-4 text-base font-medium [&::-webkit-details-marker]:hidden">
              <h2 className="text-base font-medium">{tp(`faq.${k}.q`)}</h2>
              <Plus className="size-4 shrink-0 transition-transform duration-200 group-open:rotate-45" aria-hidden="true" />
            </summary>
            <p className="max-w-[65ch] pb-5 leading-relaxed text-muted-foreground">{tp(`faq.${k}.a`)}</p>
          </details>
        ))}
      </div>
      <ReadOn links={["programs", "execution", "payouts", "terms", "risk"]} />
    </main>
  );
}
